import {CommonModule} from "@angular/common";
import {booleanAttribute, Component, EventEmitter, Input, Optional, Output, ChangeDetectionStrategy} from "@angular/core";
import {MatMenu, MatMenuTrigger} from "@angular/material/menu";

import {MaterialModule} from "framework/material";
import {IconComponent} from "framework/ui/icons/icon.component";

@Component({
    selector: "cs-icon-button",
    templateUrl: "./icon-button.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        IconComponent,
        MaterialModule
    ]
})
export class IconButtonComponent
{
    @Input() csIcon: string;
    @Input() icon: string;
    @Input() ariaLabel: string; // icon-only buttons need an accessible name (WCAG button-name)
    @Input() menuTrigger: MatMenu;
    @Input() size: IconButtonSize = "large";
    @Input() buttonStyle: IconButtonStyle;
    @Input({transform: booleanAttribute}) disableRipple: boolean;
    @Input() customClass: string;
    @Input({transform: booleanAttribute}) emitClick: boolean; // for if you also need click event propagating
    @Input({transform: booleanAttribute}) disabled: boolean;
    @Output() buttonClick         = new EventEmitter<Event>();

    buttonClasses: string;

    constructor(@Optional() private m_trigger: MatMenuTrigger) {}

    ngOnChanges()
    {
        this.buttonClasses = this.generateIconButtonClasses();

        if (this.customClass)
        {
            this.buttonClasses += " " + this.customClass;
        }
    }

    onClick(event: Event): void
    {
        if (this.buttonClick.observed && !this.m_trigger && !this.emitClick)
        {
            event.stopPropagation();
        }

        this.buttonClick.emit(event);
    }

    private generateIconButtonClasses(): string
    {
        let classes: string[] = [];

        switch (this.size)
        {
            case "extraSmall":
                classes.push("cs-icon-btn-xs");
                break;

            case "small":
                classes.push("cs-icon-btn-sm");
                break;

            case "medium":
                classes.push("cs-icon-btn-med");
                break;

            case "large":
                classes.push("cs-icon-btn-lg");
                break;

            case "largeWithSmallIcon":
                classes.push("cs-icon-btn-lg-sm");
                break;

            case "extraLarge":
                classes.push("cs-icon-btn-xl");
                break;
        }

        switch (this.buttonStyle)
        {
            case "primary":
                classes.push("cs-btn-primary");
                break;

            case "secondary":
                classes.push("cs-btn-secondary");
                break;

            case "tertiary":
                classes.push("cs-btn-tertiary");
                break;
        }

        return classes.join(" ");
    }
}

export type IconButtonStyle = "primary" | "secondary" | "tertiary";
export type IconButtonSize = "extraSmall" | "small" | "medium" | "large" | "largeWithSmallIcon" | "extraLarge";
